import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput, StyleSheet } from 'react-native';
import { useAIChatHistory } from '../../hooks/useData';
import aiService from '../../services/aiService';

const AIChat = () => {
  const { data: chatHistory, loading } = useAIChatHistory();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [aiStatus, setAIStatus] = useState(null);
  
  // Initialize with chat history or default message
  useEffect(() => {
    if (chatHistory && chatHistory.length > 0) {
      setMessages(chatHistory);
    } else {
      setMessages([
        {
          id: 'welcome-1',
          type: 'ai',
          content: "Hi! I'm your Zecher AI assistant. I can help you understand your finances, analyze your spending, and provide personalized advice. What would you like to know?",
          timestamp: new Date().toISOString()
        }
      ]);
    }
  }, [chatHistory]);

  // Check AI service status on mount
  useEffect(() => {
    checkAIStatus();
  }, []);

  const checkAIStatus = async () => {
    const status = await aiService.getStatus();
    setAIStatus(status);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim()) return;
    
    const userMessage = aiService.formatChatMessage(input, 'user');
    const currentMessages = [...messages, userMessage];
    
    // Add user message immediately
    setMessages(currentMessages);
    setInput('');
    setIsTyping(true);

    try {
      // Send to AI service
      const aiResponse = await aiService.sendChatMessage(
        input, 
        currentMessages.slice(-10), // Send last 10 messages for context
        'demo_user'
      );
      
      // Add AI response
      const aiMessage = {
        ...aiResponse,
        id: aiService.generateMessageId()
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      const errorMessage = {
        id: aiService.generateMessageId(),
        type: 'ai',
        content: "I'm sorry, I encountered an error. Please try again.",
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const getSuggestions = () => {
    if (!aiStatus?.configured) {
      return [
        "How do I set up AI features?",
        "Show me my account summary",
        "Help me understand my spending"
      ];
    }
    
    return [
      "How's my spending this month?",
      "Should I pay off debt or invest?",
      "How can I save more money?",
      "Analyze my transaction patterns"
    ];
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>AI Assistant</Text>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading chat...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>AI Assistant</Text>
        <View style={[
          styles.statusBadge, 
          aiStatus?.configured ? styles.statusOnline : styles.statusOffline
        ]}>
          <Text style={[
            styles.statusText,
            aiStatus?.configured ? styles.statusTextOnline : styles.statusTextOffline
          ]}>
            {aiStatus?.configured ? '🤖 AI Online' : '⚠️ AI Setup Required'}
          </Text>
        </View>
      </View>
      
      <ScrollView style={styles.messagesContainer} contentContainerStyle={styles.messagesContent}>
        {messages.map((message) => (
          <View
            key={message.id}
            style={[
              styles.messageWrapper,
              message.type === 'user' ? styles.userMessageWrapper : styles.aiMessageWrapper
            ]}
          >
            <View
              style={[
                styles.messageBubble,
                message.type === 'user' ? styles.userMessage : styles.aiMessage
              ]}
            >
              <Text style={[
                styles.messageText,
                message.type === 'user' ? styles.userMessageText : styles.aiMessageText
              ]}>
                {message.content}
              </Text>
            </View>
          </View>
        ))}
        
        {isTyping && (
          <View style={[styles.messageWrapper, styles.aiMessageWrapper]}>
            <View style={[styles.messageBubble, styles.aiMessage]}>
              <Text style={[styles.messageText, styles.aiMessageText]}>
                <Text style={styles.typingIndicator}>●●●</Text> Thinking...
              </Text>
            </View>
          </View>
        )}
      </ScrollView>

      {/* Quick suggestions */}
      {messages.length <= 1 && (
        <View style={styles.suggestionsContainer}>
          <Text style={styles.suggestionsTitle}>Try asking:</Text>
          <View style={styles.suggestionsGrid}>
            {getSuggestions().map((suggestion, index) => (
              <TouchableOpacity
                key={index}
                style={styles.suggestionButton}
                onPress={() => handleSuggestionClick(suggestion)}
              >
                <Text style={styles.suggestionText}>{suggestion}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      <View style={styles.inputContainer}>
        <TextInput
          value={input}
          onChangeText={setInput}
          placeholder={aiStatus?.configured ? "Ask about your finances..." : "AI setup required for chat..."}
          style={[styles.textInput, !aiStatus?.configured && styles.textInputDisabled]}
          multiline={false}
          onSubmitEditing={handleSubmit}
          returnKeyType="send"
          editable={!isTyping}
        />
        <TouchableOpacity
          style={[styles.sendButton, (!input.trim() || isTyping) && styles.sendButtonDisabled]}
          onPress={handleSubmit}
          disabled={!input.trim() || isTyping}
        >
          <Text style={[styles.sendButtonText, (!input.trim() || isTyping) && styles.sendButtonTextDisabled]}>
            {isTyping ? '...' : 'Send'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    height: 500,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusOnline: {
    backgroundColor: '#D1FAE5',
  },
  statusOffline: {
    backgroundColor: '#FEE2E2',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
  },
  statusTextOnline: {
    color: '#065F46',
  },
  statusTextOffline: {
    color: '#991B1B',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#6B7280',
    fontSize: 14,
  },
  messagesContainer: {
    flex: 1,
    marginBottom: 16,
  },
  messagesContent: {
    gap: 16,
    paddingBottom: 16,
  },
  messageWrapper: {
    width: '100%',
  },
  userMessageWrapper: {
    alignItems: 'flex-end',
  },
  aiMessageWrapper: {
    alignItems: 'flex-start',
  },
  messageBubble: {
    maxWidth: '85%',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  userMessage: {
    backgroundColor: '#2563EB',
  },
  aiMessage: {
    backgroundColor: '#F3F4F6',
  },
  messageText: {
    fontSize: 14,
    lineHeight: 20,
  },
  userMessageText: {
    color: '#FFFFFF',
  },
  aiMessageText: {
    color: '#111827',
  },
  typingIndicator: {
    fontSize: 12,
    color: '#6B7280',
  },
  suggestionsContainer: {
    marginBottom: 16,
  },
  suggestionsTitle: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  suggestionsGrid: {
    gap: 6,
  },
  suggestionButton: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  suggestionText: {
    fontSize: 13,
    color: '#374151',
  },
  inputContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  textInput: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#FFFFFF',
    maxHeight: 100,
  },
  textInputDisabled: {
    backgroundColor: '#F9FAFB',
    color: '#9CA3AF',
  },
  sendButton: {
    borderRadius: 12,
    backgroundColor: '#2563EB',
    paddingHorizontal: 16,
    paddingVertical: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: '#E5E7EB',
  },
  sendButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
  },
  sendButtonTextDisabled: {
    color: '#9CA3AF',
  },
});

export default AIChat; 